import React, { useState, useEffect, useRef } from 'react';
import './CustomCitySelect.css';
import { useSelector } from 'react-redux';
import { Search } from 'lucide-react';

const CustomCitySelect = ({ value, onChange, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(value);
    const dropdownRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredCities, setFilteredCities] = useState([]);
    
    // Get cities directly from Redux
    const allCities = useSelector((state) => state.cities_reducer.cities);
    
    const handleToggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };
    
    const handleOptionClick = (option) => {
        setSelectedOption(option.name);
        onChange({ target: { value: option.id } });
        setIsOpen(false);
    };

    useEffect(() => {
        if (allCities !== "isLoading" && allCities?.result?.[0]?.cities) {
            if (searchQuery.trim()) {
                const lowerCaseQuery = searchQuery.toLowerCase();
                const filtered = allCities.result[0].cities.filter(city => 
                    city.name.toLowerCase().includes(lowerCaseQuery)
                );
                setFilteredCities(filtered);
            } else {
                setFilteredCities(allCities.result[0].cities);
            }
        }
    }, [searchQuery, allCities]);

    useEffect(() => {
        if (!value) {
            setSelectedOption(null);
        } else if (allCities !== "isLoading" && allCities?.result?.[0]?.cities) {
            const selected = allCities.result[0].cities.find(city => city.id === value);
            if (selected) {
                setSelectedOption(selected.name);
            }
        }
    }, [value, allCities]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            <div className={`custom-city-select-container ${disabled ? 'custom-disabled' : ''}`}
                ref={dropdownRef}>
                <div className="custom-city-select" onClick={handleToggleDropdown}>
                    <span className='main-label-container'>{selectedOption || 'City'}</span>
                    <span className="custom-city-select-arrow">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="#4B5563"
                            className={`w-5 h-5 ${isOpen ? 'rotate-180' : ''}`}
                        >
                            <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </span>
                </div>
                {isOpen && (
                    <div className="custom-city-select-dropmenu-container mt-1">
                        {/* Search Input */}
                        <div className='custom-city-select-search-container'>
                            <input
                                className='search-input'
                                placeholder='Search city'
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Search width={18} />
                        </div>
                        
                        {/* Options List */}
                        <div className="custom-city-select-options">
                            {allCities === "isLoading" ? (
                                <div className="custom-city-select-option disabled">
                                    <span>Loading...</span>
                                </div>
                            ) : filteredCities?.length > 0 ? (
                                filteredCities
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map((city) => (
                                        <div
                                            key={city.id}
                                            className="custom-city-select-option"
                                            onClick={() => handleOptionClick(city)}
                                        >
                                            <span>{city.name}</span>
                                        </div>
                                    ))
                            ) : (
                                <div className="custom-city-select-option disabled">
                                    <span>No data</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CustomCitySelect;